import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FeatureFlagsContext, FlagContext } from 'libs.flags.react';
import { Icon, Text, Tooltip, useLocalizeMessage } from 'libs.nucleus';
import { AclScope } from 'libs.react';
import { AuthContext, useScopeValidator, useUserRoles } from 'libs.react_internal';
import { STUDY_MANAGEMENT_FLAGS } from '../../constants/developer_flags.constants';
import { StudyConfigurationContext } from '../../contexts/study_configuration/study_configuration.context';
import { timelineNameToHash } from '../../utils/study/study.utils';
const renderLinkContent = ({ title, isSectionHeader = false, navTooltipTitle, }) => {
    return (_jsxs("div", { className: 'flex items-center gap-2', children: [_jsx(Text, { size: 'sm', className: `nucleus-flex-1 nucleus-truncate ${isSectionHeader ? 'nucleus-font-medium' : ''}`, children: title }), _jsx(Tooltip, { as: 'div', placement: 'end', title: navTooltipTitle, children: _jsx(Icon, { name: 'lockSolid', color: 'yellowDark', size: 'sm' }) })] }));
};
export const useNavigation = () => {
    const { studyId } = useParams();
    const { entityId, isInternalUser } = useContext(AuthContext);
    const { currentStudy, currentTimelineId, translationsReadOnly, isUpgradeAvailable } = useContext(StudyConfigurationContext);
    const translate = useLocalizeMessage();
    const { isFeatureEnabled } = useContext(FeatureFlagsContext);
    const isTestPlansEnabled = isFeatureEnabled('TestPlans');
    const flagManager = useContext(FlagContext);
    const isViewReportsEnabled = flagManager.getValue(STUDY_MANAGEMENT_FLAGS.view_reports);
    const isBuildReportsEnabled = flagManager.getValue(STUDY_MANAGEMENT_FLAGS.build_reports);
    const { isDataManager, isDataReviewer, isAdmin, isUserAndSiteManager, isDataAdmin, isDataUser, isSiteUser } = useUserRoles();
    const resource = `entity/${entityId}/study/${studyId}`;
    const canManageStudy = useScopeValidator(AclScope.MOM_STUDY_UPDATE, { resource });
    const canGetStudyEnvironments = useScopeValidator(AclScope.MOM_STUDY_LIST, { resource });
    const canManageSites = useScopeValidator(AclScope.MOM_ENVIRONMENT_LIST, { resource });
    const isReadOnlyMode = translationsReadOnly || isUpgradeAvailable;
    const navTooltipTitle = !isUpgradeAvailable
        ? translate('Study is in read-only mode to support translations activities.')
        : translate('Update required to unlock configuration.');
    const navItems = useMemo(() => {
        const timelines = currentStudy.getScheduleTimelines();
        const scheduleHash = timelineNameToHash(timelines[currentTimelineId]?.name ?? timelines.onboarding.name);
        const items = [
            {
                icon: 'navCube',
                links: [
                    {
                        title: translate('Schedule'),
                        path: `/studies/${studyId}/build/schedule${scheduleHash}`,
                        ignoreFragments: true,
                        hidden: !canManageStudy,
                    },
                    {
                        title: translate('Resources'),
                        path: `/studies/${studyId}/build/resources`,
                        hidden: !canManageStudy,
                    },
                ],
                path: `/studies/${studyId}/build`,
                title: !isReadOnlyMode
                    ? translate('Build')
                    : renderLinkContent({
                        title: translate('Build'),
                        navTooltipTitle,
                        isSectionHeader: true,
                    }),
                hidden: !canManageStudy,
                sectionToggleTestId: 'build-section-toggle',
            },
            {
                title: translate('Translations'),
                path: `/studies/${studyId}/translations`,
                icon: 'navTranslations',
                hidden: !canManageStudy,
            },
            {
                title: translate('Versions'),
                path: `/studies/${studyId}/versions`,
                icon: 'rectangleStack',
                hidden: !canManageStudy,
            },
            {
                title: translate('Tests'),
                path: `/studies/${studyId}/tests`,
                icon: 'window',
                sectionToggleTestId: 'tests-section-toggle',
                hidden: (!canManageStudy && !canManageSites && !canGetStudyEnvironments) || isDataUser || isSiteUser,
                links: [
                    {
                        title: translate('Test plans'),
                        path: `/studies/${studyId}/test-plans`,
                        badgeProps: { label: translate('Internal') },
                        hidden: !isTestPlansEnabled,
                    },
                ],
            },
            {
                title: translate('Review packages'),
                path: `/studies/${studyId}/review-package`,
                icon: 'packageTruck',
                hidden: !canManageStudy,
            },
            {
                title: translate('Reports'),
                path: `/studies/${studyId}/data/reports`,
                icon: 'documentChartBar',
                hidden: !(isDataUser || isUserAndSiteManager || isAdmin),
            },
            {
                title: translate('User training'),
                path: `/studies/${studyId}/data/user-training`,
                icon: 'documentChartBar',
                hidden: !(isDataUser || isUserAndSiteManager || isAdmin),
            },
            {
                title: translate('Data transfers'),
                path: `/studies/${studyId}/data/data-transfers`,
                icon: 'arrowsRightLeft',
                hidden: !(isDataManager || isAdmin || isDataAdmin),
            },
            {
                title: translate('Data changes'),
                path: `/studies/${studyId}/data/data-changes`,
                icon: 'documentChartBar',
                hidden: !(isDataManager || isAdmin || isDataAdmin),
            },
            {
                title: translate('Study manager'),
                path: `/studies/${studyId}/data/study-manager`,
                icon: 'navData',
                hidden: !(isDataUser || isUserAndSiteManager || isAdmin),
            },
            {
                title: translate('View reports'),
                path: `/studies/${studyId}/data/view-reports`,
                icon: 'navData',
                hidden: !(isViewReportsEnabled && isAdmin),
            },
            {
                title: translate('Build reports'),
                path: `/studies/${studyId}/data/build-reports`,
                icon: 'navData',
                hidden: !(isBuildReportsEnabled && isAdmin),
            },
            {
                title: translate('Deploy'),
                path: `/studies/${studyId}/deploy`,
                icon: 'navDeployment',
                hidden: (!canManageStudy && !canManageSites && !canGetStudyEnvironments) || isDataUser || isSiteUser,
            },
        ];
        if (isInternalUser) {
            items[0].links.push({
                title: translate('Extensions'),
                path: `/studies/${studyId}/build/extensions`,
                badgeProps: { label: translate('Internal') },
                hidden: !canManageStudy,
            }, {
                title: translate('Notifications'),
                path: `/studies/${studyId}/build/notifications`,
                badgeProps: { label: translate('Internal') },
                hidden: !canManageStudy,
            });
            if (flagManager.getValue(STUDY_MANAGEMENT_FLAGS.studySummary)) {
                items[0].links.push({
                    title: translate('Study summary'),
                    path: `/studies/${studyId}/build/study-summary`,
                    badgeProps: { label: translate('Internal') },
                    hidden: !canManageStudy,
                });
            }
        }
        if (flagManager.getValue(STUDY_MANAGEMENT_FLAGS.review)) {
            items[0].links.push({
                title: translate('Review'),
                path: `/studies/${studyId}/build/review`,
                hidden: !canManageStudy,
            });
        }
        return items;
    }, [
        flagManager,
        studyId,
        currentTimelineId,
        isReadOnlyMode,
        isTestPlansEnabled,
        isViewReportsEnabled,
        isBuildReportsEnabled,
        canManageStudy,
        canGetStudyEnvironments,
        canManageSites,
        isDataManager,
        isDataReviewer,
        isUserAndSiteManager,
        isAdmin,
        isDataAdmin,
        isDataUser,
        isSiteUser,
        isInternalUser,
        currentStudy,
        translate,
        navTooltipTitle,
    ]);
    return { navItems };
};
